import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Row,
  Col,
  Tooltip,
  Popconfirm,
  Input,
  Select,
  DatePicker,
  Typography
} from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../services/api';
import useDocumentTitle from '../hooks/useDocumentTitle';
import userPreferenceService from '../services/userPreference';
import dayjs from 'dayjs';
import ListHelpButton from '../components/ListHelpButton';

const { Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface LogEntry {
  level: string;
  message: string;
  context: any;
  datetime: string;
  extra: any;
}

interface SystemStatus {
  log_file_size: string;
  recent_errors_24h: number;
  php_version: string;
  laravel_version: string;
  server_time: string;
  disk_usage: {
    total: string;
    free: string;
  };
}

interface LogsResponse {
  logs: LogEntry[];
  pagination: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

const SystemLogs: React.FC = () => {
  useDocumentTitle('系统日志 - 设置');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    level: '',
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
  });

  const fetchLogs = async (page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/logs', {
        params: {
          page,
          per_page: pageSize,
          ...filters,
        },
      });

      if (response.data.success) {
        const data: LogsResponse = response.data.data;
        setLogs(data.logs);
        setPagination({
          current: data.pagination.current_page,
          pageSize: data.pagination.per_page,
          total: data.pagination.total,
        });
      }
    } catch (error) {
      message.error('获取日志失败');
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    const next = { search: '', level: '', dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null };
    setFilters(next);
    fetchLogs(1, pagination.pageSize);
  };

  const handleClearLogs = async () => {
    try {
      const response = await api.post('/admin/logs/clear');
      if (response.data.success) {
        message.success('日志已清空');
        fetchLogs();
      }
    } catch (error) {
      message.error('清空日志失败');
    }
  };

  const handleDownloadLogs = async () => {
    try {
      const response = await api.get('/admin/logs/download', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `errors_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.log`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('日志下载成功');
    } catch (error) {
      message.error('下载日志失败');
    }
  };


  const getLevelIcon = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'info':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <ExclamationCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'processing';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<LogEntry> = [
    {
      title: '级别',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level: string) => (
        <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
          {level.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'ERROR', value: 'error' },
        { text: 'WARNING', value: 'warning' },
        { text: 'INFO', value: 'info' },
      ],
    },
    {
      title: '时间',
      dataIndex: 'datetime',
      key: 'datetime',
      width: 180,
      render: (datetime: string) => (
        <Text code>{dayjs(datetime).format('YYYY-MM-DD HH:mm:ss')}</Text>
      ),
      sorter: true,
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: {
        showTitle: false,
      },
      render: (message: string) => (
        <Tooltip title={message} placement="topLeft">
          <Text style={{ maxWidth: 400, display: 'block' }}>{message}</Text>
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              Modal.info({
                title: '日志详情',
                width: 800,
                content: (
                  <div>
                    <Paragraph>
                      <Text strong>级别:</Text> <Tag color={getLevelColor(record.level)}>{record.level}</Tag>
                    </Paragraph>
                    <Paragraph>
                      <Text strong>时间:</Text> {dayjs(record.datetime).format('YYYY-MM-DD HH:mm:ss')}
                    </Paragraph>
                    <Paragraph>
                      <Text strong>消息:</Text> {record.message}
                    </Paragraph>
                    {Object.keys(record.context).length > 0 && (
                      <Paragraph>
                        <Text strong>上下文:</Text>
                        <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 8 }}>
                          {JSON.stringify(record.context, null, 2)}
                        </pre>
                      </Paragraph>
                    )}
                    {Object.keys(record.extra).length > 0 && (
                      <Paragraph>
                        <Text strong>额外信息:</Text>
                        <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 8 }}>
                          {JSON.stringify(record.extra, null, 2)}
                        </pre>
                      </Paragraph>
                    )}
                  </div>
                ),
              });
            }}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        const savedPageSize = await userPreferenceService.getPageSizeSettings('systemLogs');
        const pageSizeToUse = savedPageSize && savedPageSize > 0 ? savedPageSize : pagination.pageSize;
        setPagination(prev => ({ ...prev, pageSize: pageSizeToUse, current: 1 }));
        fetchLogs(1, pageSizeToUse);
      } catch {
        fetchLogs();
      }
    })();
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={(
          <Space size="small">
            <span>系统日志管理</span>
            <ListHelpButton page_key="system_logs" />
          </Space>
        )}
        style={{ marginBottom: '24px' }}
        extra={
          <Space>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                fetchLogs(pagination.current, pagination.pageSize);
              }}
            >
              刷新
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadLogs}
            >
              下载日志
            </Button>
            <Popconfirm
              title="确定要清空所有日志吗？"
              description="此操作不可恢复，建议先下载备份。"
              onConfirm={handleClearLogs}
              okText="确定"
              cancelText="取消"
            >
              <Button danger icon={<DeleteOutlined />}>
                清空日志
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={4}>
            <Search
              placeholder="搜索日志消息"
              allowClear
              onSearch={(value) => {
                setFilters({ ...filters, search: value });
                fetchLogs(1, pagination.pageSize);
              }}
            />
          </Col>
          <Col span={2}>
            <Select
              placeholder="选择级别"
              allowClear
              showSearch
              style={{ width: '100%' }}
              onChange={(value) => {
                setFilters({ ...filters, level: value || '' });
                fetchLogs(1, pagination.pageSize);
              }}
            >
              <Option value="error">ERROR</Option>
              <Option value="warning">WARNING</Option>
              <Option value="info">INFO</Option>
            </Select>
          </Col>
          <Col span={4}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={(dates) => {
                setFilters({ ...filters, dateRange: dates as [dayjs.Dayjs, dayjs.Dayjs] | null });
                fetchLogs(1, pagination.pageSize);
              }}
            />
          </Col>
          <Col span={6} style={{ display: 'flex', justifyContent: 'end' }} />
        </Row>

        {(filters.search || filters.level || filters.dateRange) && (
          <Row style={{ marginBottom: '16px' }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {filters.search && (
                    <Tag
                      closable
                      onClose={() => {
                        const next = { ...filters, search: '' };
                        setFilters(next);
                        fetchLogs(1, pagination.pageSize);
                      }}
                    >
                      搜索: {filters.search}
                    </Tag>
                  )}
                  {filters.level && (
                    <Tag
                      closable
                      onClose={() => {
                        const next = { ...filters, level: '' };
                        setFilters(next);
                        fetchLogs(1, pagination.pageSize);
                      }}
                    >
                      级别: {filters.level.toUpperCase()}
                    </Tag>
                  )}
                  {filters.dateRange && (
                    <Tag
                      closable
                      onClose={() => {
                        const next = { ...filters, dateRange: null as any };
                        setFilters(next);
                        fetchLogs(1, pagination.pageSize);
                      }}
                    >
                      时间: {filters.dateRange[0]?.format('YYYY-MM-DD')} ~ {filters.dateRange[1]?.format('YYYY-MM-DD')}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={logs}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (page, pageSize) => {
              if (pageSize !== pagination.pageSize) {
                userPreferenceService.savePageSizeSettings('systemLogs', pageSize).catch(() => {});
              }
              fetchLogs(page, pageSize);
            },
          }}
          rowKey={(record, index) => `${record.datetime}-${index}`}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default SystemLogs;
